import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import FormBlock from "../../../Components/Inputs/FormBlock";
import instance from "../../../Interceptor/api_instance";
import { Spinner } from "../../../Components/LoadingAnimations/LoadingAnimations";
import { ActionConfirm } from "../../../Components/Modals/ActionResponses";
import SupplierSearch from "../../../Components/Inputs/SupplierSearch";

const EmptyItem = () => ({ description: "", sku: "", cost: "", quantity: "1" });

const InvoiceDetails = ({ invoice, close, onUpdate }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    invoice_number: invoice.invoice_number || "",
    invoiced_at: invoice.invoiced_at,
    supplier: invoice.supplier
      ? { value: invoice.supplier.id, label: invoice.supplier.name }
      : null,
    purchase_order: invoice.purchase_order || "",
    notes: invoice.notes || "",
    items: (invoice.items || []).map((it) => ({
      description: it.description,
      sku: it.sku,
      cost: it.cost,
      quantity: it.quantity,
    })),
    newFiles: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const updateItem = (index, key, value) => {
    setForm((p) => {
      const items = [...p.items];
      items[index] = { ...items[index], [key]: value };
      return { ...p, items };
    });
  };

  const addItem = () =>
    setForm((p) => ({ ...p, items: [...p.items, EmptyItem()] }));
  const removeItem = (index) =>
    setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== index) }));

  const onNewFilesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setForm((p) => ({ ...p, newFiles: [...p.newFiles, ...files] }));
    e.target.value = null;
  };

  const removeNewFile = (index) =>
    setForm((p) => ({
      ...p,
      newFiles: p.newFiles.filter((_, i) => i !== index),
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("invoice_number", form.invoice_number);
      fd.append("purchase_order", form.purchase_order || "");
      fd.append("notes", form.notes || "");
      fd.append("invoiced_at", form.invoiced_at);
      fd.append("supplier", form.supplier.value);
      fd.append(
        "items",
        JSON.stringify(
          form.items.map((it) => ({
            description: it.description,
            sku: it.sku || null,
            cost: Number(it.cost),
            quantity: Number(it.quantity),
          })),
        ),
      );

      form.newFiles.forEach((f) => fd.append("files", f));

      const res = await instance.patch(
        `safety/update-invoice/${invoice.id}`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.status === 200) {
        onUpdate && onUpdate(res.data);
        close();
      } else {
        setError("Unexpected response from server");
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const res = await instance.delete(`safety/delete-invoice/${invoice.id}`);
      if (res.status === 200) {
        onUpdate && onUpdate(null, { deleted: true, id: invoice.id });
        close();
      } else {
        setError("Unexpected response from server");
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="page-mask page-mask-animation">
      <div
        className="edit-modal white-cont"
        style={{ width: 800, maxWidth: "95%" }}
      >
        <h2 className="global-h2">{t("forms.type.invoice.details")}</h2>
        {error && <p className="error">{error}</p>}
        <form onSubmit={handleSave}>
          <div style={{ display: "flex", gap: 12 }}>
            <FormBlock
              label={t("Invoice Number")}
              name="invoice_number"
              value={form.invoice_number}
              update={updateField}
              isRequired={true}
            />
            <FormBlock
              label={t("Purchase Order")}
              name="purchase_order"
              value={form.purchase_order}
              update={updateField}
              isRequired={false}
            />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <FormBlock
              type={"date"}
              label={t("Invoiced At")}
              name={"invoiced_at"}
              value={form.invoiced_at}
              update={updateField}
              isRequired={true}
            />
            <div className="form-block">
              <label className="required">{t("Supplier")}</label>
              <div className="input-cont">
                <SupplierSearch
                  selectedOption={form.supplier}
                  onSelect={(option) =>
                    setForm((p) => ({ ...p, supplier: option }))
                  }
                />
              </div>
            </div>
          </div>
          <h4 className="global-h4">{t("forms.type.invoice.items")}</h4>
          {form.items.map((row, idx) => (
            <div key={idx} className="form-row">
              <FormBlock
                label={t("Description")}
                name={`description-${idx}`}
                value={row.description}
                update={(e) => updateItem(idx, "description", e.target.value)}
                isRequired={true}
              />
              <FormBlock
                label={t("SKU (optional)")}
                name={`sku-${idx}`}
                value={row.sku}
                update={(e) => updateItem(idx, "sku", e.target.value)}
                isRequired={false}
              />
              <div className="form-block">
                <label className="input-label required">{t("Cost")}</label>
                <div className="input-cont">
                  <input
                    className="text-input with-border"
                    type="number"
                    name={`cost-${idx}`}
                    value={row.cost}
                    onChange={(e) => updateItem(idx, "cost", e.target.value)}
                    required
                    step={"0.01"}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <FormBlock
                label={t("Quantity")}
                name={`quantity-${idx}`}
                value={row.quantity}
                update={(e) => updateItem(idx, "quantity", e.target.value)}
                isRequired={true}
                type="number"
                step="1"
                placeholder="1"
                min={1}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => removeItem(idx)}
                  disabled={form.items.length === 1}
                >
                  {t("buttons.remove")}
                </button>
              </div>
            </div>
          ))}
          <div className="form-row">
            <button type="button" className="change-pwd-btn" onClick={addItem}>
              {t("Add Item")}
            </button>
          </div>

          <h4 className="global-h4">{t("forms.type.invoice.files")}</h4>
          <div className="form-row">
            <input
              type="file"
              accept="application/pdf,image/*"
              capture
              multiple
              onChange={onNewFilesChange}
            />
          </div>
          {form.newFiles.length > 0 && (
            <div className="file-previews">
              {form.newFiles.map((f, i) => (
                <div key={i} className="file-preview">
                  <div className="file-icon">
                    {f.type && f.type.startsWith("image/") ? "IMG" : "PDF"}
                  </div>
                  <div>{f.name}</div>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => removeNewFile(i)}
                  >
                    {t("buttons.remove")}
                  </button>
                </div>
              ))}
            </div>
          )}

          <h4 className="global-h4">
            {t("forms.type.invoice.existing-files")}
          </h4>
          <div className="file-list">
            {(invoice.files || []).map((f) => (
              <div key={f.id} className="file-row">
                <a
                  href={f.blob_url_sas}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {f.original_name}
                </a>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            {isSaving ? (
              <Spinner />
            ) : (
              <>
                <button className="change-pwd-btn all-btn" type="submit">
                  {t("buttons.save") || "Save"}
                </button>
                <button
                  className="error-btn all-btn"
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  style={{ marginLeft: 8 }}
                >
                  {t("buttons.delete") || "Delete"}
                </button>
                <button
                  className="cancel-btn all-btn"
                  type="button"
                  onClick={close}
                >
                  {t("buttons.cancel")}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
      {confirmDelete && (
        <ActionConfirm
          title={t("forms.type.invoice.delete.title")}
          message={t("forms.type.invoice.delete.confirm")}
          warning={null}
          confirm={() => handleDelete()}
          close={() => setConfirmDelete(false)}
          requestLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default InvoiceDetails;
